/* voyado-predictive-search.js
 * Hooks into Bogart's <search-drawer> component.
 * Replaces native predictive search with Voyado autocomplete API.
 * Requests go through window.elevate.query.autocomplete() (keys/market/locale
 * auto-attached); clicks via window.elevate.notify.click().
 */

// The Elevate SDK (window.elevate) handles all request plumbing — cluster URL,
// market/locale/touchpoint, and customerKey/sessionKey (incl. the logged-in key
// via its session handler) are auto-attached to every query.* / notify.* call.
const DEBOUNCE_MS = 200;

// query.* has no AbortController, so guard against out-of-order autocomplete
// renders with a monotonic sequence — only the latest request is rendered.
let autocompleteSeq = 0;

const drawer = document.querySelector("search-drawer");
const input = drawer?.querySelector(".search-drawer__input");
const clearBtn = drawer?.querySelector("[data-search-clear]");
const submitBtn = drawer?.querySelector(".search-drawer__submit");

if (drawer && input) {
  window.voyadoElevateReady.then(() => {
    if (!window.elevate) {
      console.warn("Voyado predictive search: window.elevate not available");
      return;
    }
    init();
  });
}

function init() {
  fetchPopular();

  let debounceTimer = null;

  input.addEventListener("input", () => {
    clearTimeout(debounceTimer);
    // Preserve a trailing space: Elevate autocomplete treats "jeans " (the word
    // is complete — advance to the next term / full-phrase matches) differently
    // from "jeans" (complete this word as a prefix). Strip only leading space;
    // a whitespace-only value collapses to empty.
    const q = input.value.trimStart();
    const isEmpty = q.trim().length === 0;
    if (clearBtn) clearBtn.hidden = isEmpty;
    debounceTimer = setTimeout(
      () => fetchAutocomplete(isEmpty ? "" : q),
      DEBOUNCE_MS
    );
  });

  clearBtn?.addEventListener("click", () => {
    input.value = "";
    if (clearBtn) clearBtn.hidden = true;
    showEmpty();
    input.focus();
  });

  submitBtn?.addEventListener("click", () => {
    const q = input.value.trim();
    if (q) {
      saveRecentSearch(q);
      window.location.href = `/search?q=${encodeURIComponent(q)}&type=product`;
    }
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      const q = input.value.trim();
      if (q) {
        saveRecentSearch(q);
        window.location.href = `/search?q=${encodeURIComponent(q)}&type=product`;
      }
    }
  });

  showEmpty();
}

// No AbortController here — fetchPopular runs once on init and populates the
// default/empty state. It's intentionally fire-and-forget.
async function fetchPopular() {
  const section = drawer?.querySelector("[data-popular-products]");
  if (!section) return;

  try {
    // keys/market/locale/touchpoint auto-attached by the SDK.
    const data = await window.elevate.query.autocomplete({ q: "" }, { contentLists: [] });

    const phrases = data.phraseSuggestions ?? [];
    if (phrases.length) {
      const phrasesSection = drawer?.querySelector("[data-popular-phrases]");
      const phrasesList = drawer?.querySelector("[data-popular-phrases-list]");
      if (phrasesSection && phrasesList) {
        phrasesList.innerHTML = phrases
          .slice(0, 3)
          .map((ph) => {
            const q = ph.q ?? String(ph);
            return `<button class="search-drawer__pill" type="button" data-suggestion="${escA(q)}">${esc(q)}</button>`;
          })
          .join("");
        phrasesList.querySelectorAll("[data-suggestion]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const q = btn.dataset.suggestion;
            saveRecentSearch(q);
            window.location.href = `/search?q=${encodeURIComponent(q)}&origin=PHRASE_SUGGEST&type=product`;
          });
        });
        phrasesSection.hidden = false;
      }
    }

    const products =
      data.productSuggestions ?? data.productGroups ?? data.products ?? [];
    if (!products.length) return;

    const list = section.querySelector(".search-drawer__product-list");
    if (!list) return;

    list.innerHTML = products
      .slice(0, 3)
      .map((g) => {
        const p = g.products?.[0] ?? g;
        const url = safeUrl(p.link);
        const title = p.title ?? "";
        const vendor = p.brand ?? "";
        const sp = p.sellingPrice?.min ?? null;
        const raw = p.imageInfo?.images?.[0]?.sources?.[0]?.url ?? null;
        const img = raw
          ? resizedUrl(safeUrl(raw.startsWith("http") ? raw : `https:${raw}`))
          : null;
        const ticket = g.ticket ?? p.ticket ?? "";
        return `<li>
        <a class="search-drawer__product-link" href="${escA(url)}"${ticket ? ` data-voyado-ticket="${escA(ticket)}"` : ""}>
          <div class="search-drawer__product-img-wrap">
            ${img ? `<img class="search-drawer__product-img" src="${escA(img)}" alt="${escA(title)}" loading="lazy" width="80" height="80">` : ""}
          </div>
          <div>
            ${vendor ? `<p class="search-drawer__product-name" style="font-size:var(--text-xs);color:var(--color-text-muted)">${esc(vendor)}</p>` : ""}
            <p class="search-drawer__product-name">${esc(title)}</p>
            ${sp !== null ? `<p class="search-drawer__product-price">${Math.round(sp)} kr</p>` : ""}
          </div>
        </a>
      </li>`;
      })
      .join("");

    list.querySelectorAll("a[data-voyado-ticket]").forEach((a) => {
      a.addEventListener("click", () => sendClick(a.dataset.voyadoTicket));
    });

    section.hidden = false;
  } catch {}
}

async function fetchAutocomplete(q) {
  if (!q) {
    showEmpty();
    return;
  }

  // The library has no request abort, so tag each request and only render if
  // it's still the latest — drops stale responses from earlier keystrokes.
  const seq = ++autocompleteSeq;

  try {
    // keys/market/locale/touchpoint auto-attached by the SDK.
    const data = await window.elevate.query.autocomplete(
      { q },
      { contentLists: [{ id: "content" }] }
    );
    if (seq !== autocompleteSeq) return; // a newer query superseded this one
    renderResults(data, q);
  } catch (e) {
    if (seq !== autocompleteSeq) return;
    console.error("Voyado autocomplete:", e);
    showNoResults();
  }
}

function renderResults(data, q) {
  const phrases = data.phraseSuggestions ?? data.phrases ?? [];
  const products =
    data.productSuggestions ?? data.productGroups ?? data.products ?? [];
  const articles = data.contentLists?.[0]?.items ?? [];

  const emptyEl = drawer.querySelector("[data-search-empty]");
  const resultsEl = drawer.querySelector("[data-search-results]");
  const noResults = drawer.querySelector("[data-search-no-results]");

  if (!phrases.length && !products.length && !articles.length) {
    showNoResults();
    return;
  }

  if (emptyEl) emptyEl.hidden = true;
  if (resultsEl) resultsEl.hidden = false;
  if (noResults) noResults.hidden = true;

  const suggestSection = drawer.querySelector(
    "[data-search-suggestions-section]"
  );
  const suggestList = drawer.querySelector("[data-search-suggestions]");
  if (suggestSection && suggestList) {
    if (phrases.length) {
      suggestList.innerHTML = phrases
        .slice(0, 5)
        .map((ph) => {
          const suggestion = ph.q ?? ph.phrase ?? String(ph);
          const display = ph.highlighted
            ? ph.highlighted.replace(/\{([^}]+)\}|([^{]+)/g, (_, m, p) =>
                m !== undefined ? `<mark>${esc(m)}</mark>` : esc(p)
              )
            : esc(suggestion);
          return `<li><button class="search-drawer__suggestion" type="button" data-suggestion="${escA(suggestion)}">${display}</button></li>`;
        })
        .join("");
      suggestSection.hidden = false;

      suggestList.querySelectorAll("[data-suggestion]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const q = btn.dataset.suggestion;
          saveRecentSearch(q);
          window.location.href = `/search?q=${encodeURIComponent(q)}&origin=PHRASE_SUGGEST&type=product`;
        });
      });
    } else {
      suggestSection.hidden = true;
    }
  }

  const divider = drawer.querySelector("[data-search-divider]");
  if (divider) divider.hidden = !(phrases.length && products.length);

  const productsSection = drawer.querySelector(
    "[data-search-products-section]"
  );
  const productsList = drawer.querySelector("[data-search-products]");
  if (productsSection && productsList) {
    if (products.length) {
      productsList.innerHTML = products
        .slice(0, 4)
        .map((g) => {
          const p = g.products?.[0] ?? g;
          const url = safeUrl(p.link);
          const title = p.title ?? "";
          const vendor = p.brand ?? "";
          const sp = p.sellingPrice?.min ?? null;
          const raw = p.imageInfo?.images?.[0]?.sources?.[0]?.url ?? null;
          const img = raw
            ? resizedUrl(safeUrl(raw.startsWith("http") ? raw : `https:${raw}`))
            : null;
          const ticket = g.ticket ?? p.ticket ?? "";

          return `<li>
          <a class="search-drawer__product-link" href="${escA(url)}"${ticket ? ` data-voyado-ticket="${escA(ticket)}"` : ""}>
            <div class="search-drawer__product-img-wrap">
              ${img ? `<img class="search-drawer__product-img" src="${escA(img)}" alt="${escA(title)}" loading="lazy" width="80" height="80">` : ""}
            </div>
            <div>
              ${vendor ? `<p class="search-drawer__product-name" style="font-size:var(--text-xs);color:var(--color-text-muted)">${esc(vendor)}</p>` : ""}
              <p class="search-drawer__product-name">${esc(title)}</p>
              ${sp !== null ? `<p class="search-drawer__product-price">${Math.round(sp)} kr</p>` : ""}
            </div>
          </a>
        </li>`;
        })
        .join("");
      productsSection.hidden = false;

      productsList.querySelectorAll("a[data-voyado-ticket]").forEach((a) => {
        a.addEventListener("click", () => sendClick(a.dataset.voyadoTicket));
      });
    } else {
      productsSection.hidden = true;
    }
  }

  const articlesSection = drawer.querySelector("[data-search-articles-section]");
  const articlesList = drawer.querySelector("[data-search-articles]");
  const articlesDivider = drawer.querySelector("[data-search-articles-divider]");
  if (articlesSection && articlesList) {
    if (articles.length) {
      articlesList.innerHTML = articles
        .slice(0, 3)
        .map((a) => {
          const url = safeUrl(a.link);
          const title = a.title ?? "";
          const ticket = a.ticket ?? "";
          return `<li>
        <a class="search-drawer__article" href="${escA(url)}"${ticket ? ` data-voyado-ticket="${escA(ticket)}"` : ""}>
          <p class="search-drawer__article-title">${esc(title)}</p>
        </a>
      </li>`;
        })
        .join("");
      articlesSection.hidden = false;
      if (articlesDivider)
        articlesDivider.hidden = !(phrases.length || products.length);
      articlesList.querySelectorAll("a[data-voyado-ticket]").forEach((a) => {
        a.addEventListener("click", () => sendClick(a.dataset.voyadoTicket));
      });
    } else {
      articlesSection.hidden = true;
      if (articlesDivider) articlesDivider.hidden = true;
    }
  }
}

function showEmpty() {
  renderRecentSearches();
  const emptyEl = drawer.querySelector("[data-search-empty]");
  const resultsEl = drawer.querySelector("[data-search-results]");
  if (emptyEl) emptyEl.hidden = false;
  if (resultsEl) resultsEl.hidden = true;
}

function renderRecentSearches() {
  const section = drawer?.querySelector("[data-recent-searches]");
  const list = drawer?.querySelector("[data-recent-searches-list]");
  if (!section || !list) return;

  const searches = getRecentSearches();
  if (!searches.length) {
    section.hidden = true;
    return;
  }

  list.innerHTML = searches
    .map(
      (q) =>
        `<button class="search-drawer__pill" type="button" data-recent-search="${escA(q)}">${esc(q)}</button>`
    )
    .join("");

  list.querySelectorAll("[data-recent-search]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const q = btn.dataset.recentSearch;
      saveRecentSearch(q);
      window.location.href = `/search?q=${encodeURIComponent(q)}&origin=RECENT_SEARCH&type=product`;
    });
  });

  section.hidden = false;
}

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem("voyado_recent_searches") ?? "[]");
  } catch {
    return [];
  }
}

function saveRecentSearch(q) {
  if (!q) return;
  try {
    const k = "voyado_recent_searches";
    const arr = JSON.parse(localStorage.getItem(k) ?? "[]");
    localStorage.setItem(
      k,
      JSON.stringify([q, ...arr.filter((s) => s !== q)].slice(0, 5))
    );
  } catch {}
}

function showNoResults() {
  const emptyEl = drawer.querySelector("[data-search-empty]");
  const resultsEl = drawer.querySelector("[data-search-results]");
  const noResults = drawer.querySelector("[data-search-no-results]");
  if (emptyEl) emptyEl.hidden = true;
  if (resultsEl) resultsEl.hidden = false;
  if (noResults) noResults.hidden = false;
}

function sendClick(ticket) {
  if (!ticket || !window.elevate?.notify) return;
  try {
    sessionStorage.setItem("voyado_pending_atc_ticket", ticket);
  } catch {}
  // notify.click sends with keepalive and auto-attaches market + keys.
  window.elevate.notify.click(ticket);
}

function esc(s) {
  return String(s).replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
  );
}
function escA(s) {
  return String(s).replace(/["']/g, (c) => (c === '"' ? "&quot;" : "&#39;"));
}
function safeUrl(u) {
  const s = String(u ?? "");
  return s.startsWith("/") ||
    s.startsWith("https://") ||
    s.startsWith("http://")
    ? s
    : "#";
}

// Suggestion thumbnails render at 80x80, so 160px covers 2x retina without
// the master-resolution file Voyado's API returns.
function resizedUrl(url, width = 160) {
  if (!url || url === "#") return url;
  return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
}
